import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { stateOptions, CountryCodes, countryOptions } from '@frontend/geography';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  FormRow,
  Text,
  PhoneField,
  TextField,
  TextareaField,
  useForm,
  SpinningLoader,
  styles,
  DateField,
  DropdownField,
  EmailField,
  useAlert,
  Heading,
  PostalCodeLocale,
  PostalCodeField,
} from '@frontend/design-system';
import { useManagePersonListContext } from './PersonSelectorContext';
import { personSelectorStyles } from './styles';

export const CustomContact = () => {
  const alert = useAlert();
  const { t } = useTranslation('personSelector');
  const [isSaving, setIsSaving] = useState(false);
  const {
    isLoading,
    closePopoverDialog,
    shouldRenderNotes,
    formFields,
    clientLocationIds,
    addContactScreenTitle,
    handleAddCustomerContact,
    addToListButtonTrackingId,
    cancelButtonTrackingId,
    patientWritebackSourceTenants,
    shouldAddAndLink,
  } = useManagePersonListContext();

  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isIntegratedOffice, integrations } = DataSourcesQueries.useGetAccessibleIntegrations();

  const shouldRenderDataSourceDropdown = (integrations ?? []).length > 1;
  const shouldRenderSourceTenantDropdown = Number(patientWritebackSourceTenants?.length) > 1;
  const isV3Integrated = isScheduleV3FlagEnabled && isIntegratedOffice;

  const { formProps, getFieldProps, isComplete, values, seedValues } = useForm({
    fields: {
      firstName: {
        type: 'text',
        value: formFields?.firstName?.value ?? '',
        required: !!formFields?.firstName?.required,
      },
      lastName: {
        type: 'text',
        value: formFields?.lastName?.value ?? '',
        required: !!formFields?.lastName?.required,
      },
      phoneNumber: {
        type: 'phone',
        value: formFields?.phoneNumber?.value ?? '',
        required: !!formFields?.phoneNumber?.required,
      },
      notes: {
        type: 'text',
        value: formFields?.notes?.value ?? '',
        required: !!formFields?.notes?.required,
      },
      dateOfBirth: {
        type: 'date',
        value: formFields?.dateOfBirth?.value ?? '',
        required: !!formFields?.dateOfBirth?.required,
      },
      gender: {
        type: 'dropdown',
        value: formFields?.gender?.value ?? '',
        required: !!formFields?.gender?.required,
      },
      email: {
        type: 'email',
        value: formFields?.email?.value ?? '',
        required: !!formFields?.email?.required,
      },
      source: {
        type: 'dropdown',
        value: formFields?.source?.value ?? '',
        required: !!formFields?.source?.required && shouldRenderDataSourceDropdown,
      },
      clientLocationId: {
        type: 'dropdown',
        value: formFields?.clientLocationId?.value ?? '',
      },
      street: {
        type: 'text',
        value: formFields?.street?.value ?? '',
        required: !!formFields?.street?.required,
      },
      city: {
        type: 'text',
        value: formFields?.city?.value ?? '',
        required: !!formFields?.city?.required,
      },
      country: {
        type: 'text',
        value: (formFields?.country?.value as CountryCodes) || CountryCodes.USA,
        required: !!formFields?.country?.required,
      },
      state: {
        type: 'dropdown',
        value: formFields?.state?.value ?? '',
        required: !!formFields?.state?.required,
      },
      postalCode: {
        type: 'text',
        value: formFields?.postalCode?.value ?? '',
        required: !!formFields?.postalCode?.required,
      },
    },
  });

  const filteredClientLocations = useMemo(() => {
    if (!values.source) return [];
    return clientLocationIds?.filter((clientLocation) => clientLocation.SourceID === values.source);
  }, [values.source]);

  const handleAddPatient = async () => {
    setIsSaving(true);

    try {
      if (isComplete && handleAddCustomerContact) {
        const payload = {
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          phoneNumber: values.phoneNumber ?? '',
          dateOfBirth: values.dateOfBirth ?? '',
          email: values.email ?? '',
          gender: values.gender ?? '',
          source: values.source ?? '',
          notes: values.notes ?? '',
          clientLocationId: values.clientLocationId ?? '',
          street: values.street ?? '',
          city: values.city ?? '',
          state: values.state ?? '',
          postalCode: values.postalCode ?? '',
          country: values.country ?? '',
        };
        await handleAddCustomerContact(payload);
      }
    } catch (err) {
      alert.error(t('Error Adding Custom Contact'));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // V3 integrated locations doesn't use source and clientLocationId, instead uses sourceTenants
    if (isV3Integrated) {
      return;
    }

    if (integrations?.length === 1) {
      seedValues({
        ...values,
        source: integrations[0].SourceID,
      });
    }
    if (filteredClientLocations?.length === 1) {
      seedValues({
        ...values,
        clientLocationId: filteredClientLocations[0].ClientLocationID,
      });
    }
  }, [integrations, filteredClientLocations, isV3Integrated]);

  useEffect(() => {
    if (patientWritebackSourceTenants?.length === 1 && isV3Integrated) {
      seedValues({
        ...values,
        source: patientWritebackSourceTenants[0].sourceTenantId,
      });
    }
  }, [patientWritebackSourceTenants, isV3Integrated]);

  const shouldRenderClientLocationDropdown =
    isIntegratedOffice &&
    !!formFields.clientLocationId &&
    !!filteredClientLocations &&
    filteredClientLocations.length > 1;

  if (isLoading || isSaving)
    return (
      <div css={css([{ height: 300 }, styles.flexCenter])}>
        <SpinningLoader />
      </div>
    );

  // For integrated offices, do not show mandatory indicators as all fields are mandatory
  const shouldShowMandatoryIndicators = !isIntegratedOffice;

  return (
    <div css={{ paddingTop: theme.spacing(1) }}>
      <div css={personSelectorStyles.header}>
        <Heading level={3} css={{ lineHeight: '24px' }}>
          {addContactScreenTitle}
        </Heading>
      </div>
      <form {...formProps} css={personSelectorStyles.content}>
        {!!formFields.source && !isV3Integrated && shouldRenderDataSourceDropdown && (
          <FormRow>
            <DropdownField {...getFieldProps('source')} label={t('Source')}>
              {integrations?.map(({ SourceID, SourceName, PracticeManagementSystem }) => (
                <DropdownField.Option value={SourceID} key={SourceID}>
                  <Text as='span' css={{ marginRight: theme.spacing(1) }}>
                    {SourceName}
                  </Text>
                  <Text as='span' color='light' size='small'>
                    {PracticeManagementSystem}
                  </Text>
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
        )}
        {!!formFields.source && isV3Integrated && shouldRenderSourceTenantDropdown && (
          <FormRow>
            <>
              {!!patientWritebackSourceTenants?.length && (
                <DropdownField label={t('Location')} {...getFieldProps('source')}>
                  {patientWritebackSourceTenants?.map(({ sourceTenantId = '', name, pmsName }) => (
                    <DropdownField.Option value={sourceTenantId} key={name}>
                      <Text as='span' css={{ marginRight: theme.spacing(1) }}>
                        {name}
                      </Text>
                      <Text as='span' color='light' size='small'>
                        {pmsName}
                      </Text>
                    </DropdownField.Option>
                  ))}
                </DropdownField>
              )}
            </>
          </FormRow>
        )}
        {!isV3Integrated && shouldRenderClientLocationDropdown && (
          <FormRow>
            <DropdownField {...getFieldProps('clientLocationId')} label={t('Location')}>
              {filteredClientLocations?.map(({ ClientLocationID, Name }) => (
                <DropdownField.Option value={ClientLocationID} key={ClientLocationID}>
                  <Text as='span' css={{ marginRight: theme.spacing(1) }}>
                    {Name}
                  </Text>
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
        )}
        <FormRow cols={[50, 50]}>
          <TextField
            {...getFieldProps('firstName')}
            label={shouldShowMandatoryIndicators ? t('*First Name') : t('First Name')}
            placeholder={shouldShowMandatoryIndicators ? t('*First Name') : t('First Name')}
          />
          <TextField
            {...getFieldProps('lastName')}
            label={shouldShowMandatoryIndicators ? t('*Last Name') : t('Last Name')}
            placeholder={shouldShowMandatoryIndicators ? t('*Last Name') : t('Last Name')}
          />
        </FormRow>
        <FormRow cols={!!formFields.dateOfBirth && !!formFields.gender ? [50, 50] : [100]}>
          {!!formFields.dateOfBirth && (
            <DateField {...getFieldProps('dateOfBirth')} label={t('Date of Birth')} placeholder={t('Date of Birth')} />
          )}
          {!!formFields.gender && (
            <DropdownField {...getFieldProps('gender')} label={t('Gender')}>
              <DropdownField.Option value='Male'>Male</DropdownField.Option>
              <DropdownField.Option value='Female'>Female</DropdownField.Option>
              <DropdownField.Option value='Other'>Other</DropdownField.Option>
            </DropdownField>
          )}
        </FormRow>
        <FormRow cols={!!formFields.phoneNumber && !!formFields.email ? [50, 50] : [100]}>
          {!!formFields.phoneNumber && (
            <PhoneField
              {...getFieldProps('phoneNumber')}
              label={shouldShowMandatoryIndicators ? t('*Phone Number') : t('Phone Number')}
              placeholder={shouldShowMandatoryIndicators ? t('*Phone Number') : t('Phone Number')}
            />
          )}
          {!!formFields.email && (
            <EmailField
              {...getFieldProps('email')}
              label={shouldShowMandatoryIndicators ? t('*Email') : t('Email')}
              placeholder={shouldShowMandatoryIndicators ? t('*Email') : t('Email')}
            />
          )}
        </FormRow>
        {isIntegratedOffice && (
          <>
            <FormRow cols={[100]}>
              <TextField {...getFieldProps('street')} label={t('Street Address')} placeholder={t('Street Address')} />
            </FormRow>
            <FormRow cols={[50, 50]}>
              <TextField {...getFieldProps('city')} label={t('City')} placeholder={t('City')} />
              <DropdownField
                {...getFieldProps('state')}
                label={values.country === CountryCodes.Canada ? t('Province') : t('State')}
              >
                <DropdownField.Option value=''>{t('Select State')}</DropdownField.Option>
                {stateOptions[values.country as CountryCodes]?.map((state) => (
                  <DropdownField.Option key={state.value} value={state.value} searchValue={state.label}>
                    {state.label}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FormRow>
            <FormRow cols={[50, 50]}>
              <PostalCodeField
                {...getFieldProps('postalCode')}
                label={values.country === CountryCodes.Canada ? t('Postal Code') : t('Zip Code')}
                locale={values.country as PostalCodeLocale}
              />
              <DropdownField
                {...getFieldProps('country')}
                label={t('Country')}
                onChange={(e) => {
                  getFieldProps('country').onChange?.(e);
                  seedValues({ state: '', postalCode: '' });
                }}
              >
                {countryOptions.map((country) => (
                  <DropdownField.Option key={country.value} value={country.value}>
                    {country.label}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </FormRow>
          </>
        )}
        {shouldRenderNotes && (
          <FormRow>
            <TextareaField
              {...getFieldProps('notes')}
              label={t('Patient notes (optional)')}
              placeholder={t('Patient notes (optional)')}
            />
          </FormRow>
        )}
      </form>
      <ButtonBar css={personSelectorStyles.footer}>
        <Button variant='secondary' trackingId={cancelButtonTrackingId} onClick={closePopoverDialog}>
          {t('Cancel')}
        </Button>
        {shouldAddAndLink ? (
          <Button trackingId={addToListButtonTrackingId} disabled={!isComplete} onClick={handleAddPatient}>
            {t('Add & Link')}
          </Button>
        ) : (
          <Button trackingId={addToListButtonTrackingId} disabled={!isComplete} onClick={handleAddPatient}>
            <Icon name='plus-small' color='white' />
            {t('Add to List')}
          </Button>
        )}
      </ButtonBar>
    </div>
  );
};
